import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "next-themes";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { EmailAdminRouteGuard } from "./components/email/EmailAdminRouteGuard";
import { EmailVerification } from "./components/email/EmailVerification";
import { AdminRouteGuard } from "./components/auth/AdminRouteGuard";
import Index from "./pages/Index";
import SignIn from "./pages/auth/SignIn";
import SignUp from "./pages/auth/SignUp";
import ForgotPassword from "./components/auth/ForgotPassword";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/dashboard/Profile";
import Business from "./pages/dashboard/Business";
import Documents from "./pages/dashboard/Documents";
import Settings from "./pages/dashboard/Settings";
import Support from "./pages/dashboard/Support";
import UserManagement from "./pages/dashboard/UserManagement";
import Analytics from "./pages/dashboard/Analytics";
import Messages from "./pages/dashboard/Messages";
import TaxReturn from "./pages/dashboard/TaxReturn";
import Payments from "./pages/dashboard/Payments";
import ServiceManagement from "./pages/dashboard/ServiceManagement";
import PrivacyPolicy from "./pages/privacy/PrivacyPolicy";
import TermsOfService from "./pages/terms/TermsOfService";
import TaxInformation from "./pages/tax-information/TaxInformation";
import Contact from "./pages/contact/Contact";
import ABNRegistration from "./pages/abn-registration/ABNRegistration";
import GSTRegistration from "./pages/gst-registration/GSTRegistration";
import ACNRegistration from "./pages/acn-registration/ACNRegistration";
import CompanyRegistration from "./pages/company-registration/CompanyRegistration";
import ABNLookup from "./pages/abn-lookup/ABNLookup";
import Calculators from "./pages/calculators/Calculators";

// Email Management Pages
import EmailDashboard from "./pages/email";
import EmailMonitoring from "./pages/email/monitoring";
import EmailPerformance from "./pages/email/performance";
import BounceManagement from "./pages/email/bounce-management";
import ListManagement from "./pages/email/list-management";
import EmailTemplates from "./pages/email/templates";
import EmailPreferences from "./pages/email/preferences";
import EmailPrivacyPolicy from "./pages/email/privacy-policy";
import EmailTerms from "./pages/email/terms";
import EmailUnsubscribe from "./pages/email/unsubscribe";

// Public Email Preference Pages
import EmailPreference from "./pages/email-preference";
import EmailUnsubscribePublic from "./pages/email-unsubscribe";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/tax-information" element={<TaxInformation />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/abn-registration" element={<ABNRegistration />} />
            <Route path="/gst-registration" element={<GSTRegistration />} />
            <Route path="/acn-registration" element={<ACNRegistration />} />
            <Route
              path="/company-registration"
              element={<CompanyRegistration />}
            />
            <Route path="/abn-lookup" element={<ABNLookup />} />
            <Route path="/calculators" element={<Calculators />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/dashboard/profile" element={<Profile />} />
            <Route path="/dashboard/business" element={<Business />} />
            <Route path="/dashboard/documents" element={<Documents />} />
            <Route path="/dashboard/settings" element={<Settings />} />
            <Route path="/dashboard/support" element={<Support />} />
            <Route path="/dashboard/users" element={<UserManagement />} />
            <Route path="/dashboard/analytics" element={<Analytics />} />
            <Route path="/dashboard/messages" element={<Messages />} />
            <Route path="/dashboard/tax-return" element={<TaxReturn />} />
            <Route path="/dashboard/payments" element={<Payments />} />
            <Route path="/dashboard/services" element={<ServiceManagement />} />

            {/* Admin Email Dashboard Routes */}
            <Route path="/admin/email/verify" element={<EmailVerification />} />

            {/* Protected Admin Email Management Routes */}
            <Route
              path="/admin/email"
              element={
                <EmailAdminRouteGuard>
                  <EmailDashboard />
                </EmailAdminRouteGuard>
              }
            />
            <Route
              path="/admin/email/monitoring"
              element={
                <EmailAdminRouteGuard>
                  <EmailMonitoring />
                </EmailAdminRouteGuard>
              }
            />
            <Route
              path="/admin/email/performance"
              element={
                <EmailAdminRouteGuard>
                  <EmailPerformance />
                </EmailAdminRouteGuard>
              }
            />
            <Route
              path="/admin/email/bounce-management"
              element={
                <EmailAdminRouteGuard>
                  <BounceManagement />
                </EmailAdminRouteGuard>
              }
            />
            <Route
              path="/admin/email/list-management"
              element={
                <EmailAdminRouteGuard>
                  <ListManagement />
                </EmailAdminRouteGuard>
              }
            />
            <Route
              path="/admin/email/templates"
              element={
                <EmailAdminRouteGuard>
                  <EmailTemplates />
                </EmailAdminRouteGuard>
              }
            />
            <Route
              path="/admin/email/preferences"
              element={
                <EmailAdminRouteGuard>
                  <EmailPreferences />
                </EmailAdminRouteGuard>
              }
            />

            {/* Public Email Routes */}
            <Route
              path="/email/privacy-policy"
              element={<EmailPrivacyPolicy />}
            />
            <Route path="/email/terms" element={<EmailTerms />} />
            <Route path="/email/unsubscribe" element={<EmailUnsubscribe />} />

            {/* Public Email Preference Routes for AWS SES Review */}
            <Route path="/email-preference" element={<EmailPreference />} />
            <Route
              path="/email-unsubscribe"
              element={<EmailUnsubscribePublic />}
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
